'use client';

import { useState } from 'react';
import { CustomModal } from '@/components/CustomModal';
import { Button } from '@/components/ui/button';
import type { MerchSizeChart } from '../interfaces/merch';

interface SizeChartModalProps {
  open: boolean;
  onClose: () => void;
  sizeChart?: MerchSizeChart;
}

const CM_TO_IN = 0.393701;

function formatMeasure(value: number | undefined, unit: 'cm' | 'in') {
  if (value == null) return '—';
  if (unit === 'cm') return `${value} cm`;
  return `${(value * CM_TO_IN).toFixed(1)} in`;
}

export function SizeChartModal({ open, onClose, sizeChart }: SizeChartModalProps) {
  const [unit, setUnit] = useState<'cm' | 'in'>('cm');
  const rows = sizeChart?.sizes ?? [];

  return (
    <CustomModal
      isOpen={open}
      onClose={onClose}
      title="Guía de tallas"
      description="Compara las medidas del producto con una prenda que ya te quede bien."
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant={unit === 'cm' ? 'default' : 'outline'}
            onClick={() => setUnit('cm')}
          >
            Centímetros
          </Button>
          <Button
            type="button"
            size="sm"
            variant={unit === 'in' ? 'default' : 'outline'}
            onClick={() => setUnit('in')}
          >
            Pulgadas
          </Button>
        </div>

        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Este producto aún no tiene tabla de medidas publicada.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border/60">
            <table className="w-full min-w-[28rem] text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/30 text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Talla</th>
                  <th className="px-4 py-3 font-medium">Pecho</th>
                  <th className="px-4 py-3 font-medium">Cintura</th>
                  <th className="px-4 py-3 font-medium">Largo</th>
                  <th className="px-4 py-3 font-medium">Cadera</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.label} className="border-b border-border/40 last:border-0">
                    <td className="px-4 py-3 font-semibold">{row.label}</td>
                    <td className="px-4 py-3">{formatMeasure(row.chest, unit)}</td>
                    <td className="px-4 py-3">{formatMeasure(row.waist, unit)}</td>
                    <td className="px-4 py-3">{formatMeasure(row.length, unit)}</td>
                    <td className="px-4 py-3">{formatMeasure(row.hip, unit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </CustomModal>
  );
}
